import React, { useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { AVAILABLE_BRAND_FEATURES, PriceTier } from '@kouto/types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Sep } from 'components/common/styled/common-styled';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button/Button';
import { ResourceParagraph } from 'components/theme/Typography/ResourceTypography';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import {
  extractTimesFromDateTime,
  formatSessionTimeRange,
} from 'utils/dateTime';
import { PriceTierRadioSelector } from 'components/Booking/PriceTier';
import type { StayPricing } from 'components/Resource/ResourcePriceTierSelector/NonMappedPriceTierSelector';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import {
  BottomDrawer,
  BottomDrawerProps,
} from 'components/molecules/BottomDrawer';
import { isFeatureActive } from 'utils/featureFlag';
import type { ITax } from 'types/experience.types';
import ResourceMetaMobileView from './ResourceMeta/ResourceMetaMobileView';
import ResourceAccessCode, { AccessCodeHeading } from './ResourceAccessCode';

interface BottomSheetResourcePageProps extends BottomDrawerProps {
  isLoading: boolean;
  priceTiers: PriceTier[];
  stayPricing?: StayPricing | null;
  taxes?: ITax[];
  onAddToCart: () => void;
  onCheckout: () => void;
  onValidateAccessCode: (code: string) => void;
  onContinue?: () => void;
  selectedSession: {
    startDateTime: string;
    duration: number;
  };
  selectedPriceTierId?: string;
  onTierSelect: (tierId: string) => void;
  accessCodeVerified: boolean;
  accessCodeError: string;
  customCallToAction: string | null;
  isLoadingCustomQuestions?: boolean;
  /** For a continuously booked resource, the start and length picker; sessions have none. */
  sessionPicker?: React.ReactNode;
}

const BottomSheetResourcePage = ({
  priceTiers,
  stayPricing,
  taxes,
  onAddToCart,
  onCheckout,
  onContinue,
  selectedSession,
  selectedPriceTierId,
  onTierSelect,
  accessCodeVerified,
  onValidateAccessCode,
  accessCodeError,
  customCallToAction,
  isLoadingCustomQuestions,
  sessionPicker,
  ...props
}: BottomSheetResourcePageProps) => {
  const hasRemoveAddToCart = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_HIDE_ADD_TO_CART_BUTTON,
  );
  const currency = useBrandCurrency();
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();

  const { startTime, endTime } = useMemo(() => {
    const { timePart } = extractTimesFromDateTime(
      selectedSession?.startDateTime,
    );
    return formatSessionTimeRange(
      timePart.substring(0, 8),
      selectedSession.duration,
      locale,
    );
  }, [selectedSession, locale]);

  if (!currency) {
    return null;
  }

  if (!accessCodeVerified) {
    return (
      <BottomDrawer {...props} heading={<AccessCodeHeading />}>
        <ResourceAccessCode
          errorMessage={accessCodeError}
          onContinue={onValidateAccessCode}
          withHeading={false}
          isExclusive
          customCallToAction={customCallToAction}
        />
      </BottomDrawer>
    );
  }

  return (
    <BottomDrawer {...props}>
      <Flex
        direction="column"
        alignItem="flex-start"
        justifyContent="flex-start"
        gap={24}
      >
        <Flex
          direction="column"
          gap={16}
          alignItem="flex-start"
          justifyContent="flex-start"
          width="100%"
        >
          <ButtonWrapper
            direction="row"
            gap={16}
            alignItem="stretch"
            justifyContent="flex-start"
            width="100%"
          >
            {onContinue && (
              <PrimaryButton
                big
                onClick={onContinue}
                className="continue-button"
              >
                {customCallToAction || translate('continue')}
              </PrimaryButton>
            )}
            {!onContinue && (
              <>
                {!hasRemoveAddToCart && (
                  <SecondaryButton
                    disabled={isLoadingCustomQuestions}
                    onClick={onAddToCart}
                    className="add-to-cart-button"
                  >
                    {translate('addToCart')}
                  </SecondaryButton>
                )}
                <PrimaryButton
                  big
                  disabled={isLoadingCustomQuestions}
                  onClick={onCheckout}
                  className="checkout-button"
                >
                  {translate('checkout')}
                </PrimaryButton>
              </>
            )}
          </ButtonWrapper>
        </Flex>
        <ResourceMetaMobileView
          metaLabel="Date"
          value={format(
            parseISO(
              extractTimesFromDateTime(selectedSession.startDateTime).datePart,
            ),
            'PP',
            { locale },
          )}
        />
        <ResourceMetaMobileView
          metaLabel={translate('time')}
          value={`${startTime} - ${endTime}`}
        />
        {sessionPicker}
        <Sep />
        <Flex
          direction="column"
          alignItem="flex-start"
          justifyContent="flex-start"
          gap={16}
        >
          <ResourceParagraph
            className="resource-page-bottom-sheet__selected-tiers"
            fontWeight="350"
            lineHeight="normal"
          >
            {translate('tier')}
          </ResourceParagraph>
          <PriceTierRadioSelector
            value={selectedPriceTierId}
            priceTiers={priceTiers}
            stayPricing={stayPricing}
            taxes={taxes}
            currency={currency.code || ''}
            onChange={onTierSelect}
          />
        </Flex>
      </Flex>
    </BottomDrawer>
  );
};

const ButtonWrapper = styled(Flex)`
  && {
    margin-top: 24px;

    & > button {
      flex: 1;
      padding: 10px 12px;
      height: 48px;
    }
  }
`;

export default BottomSheetResourcePage;
